import React from "react";
import styles from "./styles.module.scss";
import coachnow_logo from "app/images/Logo_dark.svg";
import academy_logo from "app/images/academy-l.svg";
import orange_bg_check from "app/images/orange_bg_checkmark.svg";
import { useTranslation } from "react-i18next";
import { PromoDiscount } from "app/models/PromoOffer";
import { AcademyPlanCode } from "./PricingToggle";
import { Currency, formatPrice } from "app/utils/currency";
import DiscountedPrice from "app/components/DiscountedPrice";
import TrialPriceLine from "app/components/TrialPriceLine";
import { usePlanPricing } from "app/hooks/usePlanPricing";

interface IProps {
  planCode: AcademyPlanCode;
  currency?: Currency;
  loading?: boolean;
  isPhased?: boolean;
  // Phase amounts of the resolved schedule, in cents: first phase ($65) for 12
  // months, then the last phase (75, or 89.99 for the full-price variant).
  phasedFirstAmount?: number;
  phasedSecondAmount?: number;
  // The resolved offer's discount (from the backend), or null/undefined.
  discount?: PromoDiscount | null;
  // The plan's live base price (cents) for the currency, from the backend
  // resolution (matches the marketing table by Stripe Price nickname).
  baseAmount?: number;
  // Direct purchase: no free-trial headline, the price itself leads.
  purchase?: boolean;
  // The paywall's copy (from its GrowthBook flag, defaulting to today's).
  description: string;
  trialHeadline: string;
}

const PriceCard = ({
  planCode,
  currency = "usd",
  loading,
  isPhased = false,
  // 0 defaults: the parent always passes the backend-resolved phase amounts
  // (0 only during the initial resolve, while `loading` gates the rendering).
  phasedFirstAmount = 0,
  phasedSecondAmount = 0,
  discount,
  baseAmount,
  purchase = false,
  description,
  trialHeadline,
}: IProps) => {
  const { t } = useTranslation();
  const isYearly = planCode === "coachacademy+yearly";
  const pricing = usePlanPricing({ baseAmount, discount, isYearly });
  const { base, discountedAmount, interval, billing } = pricing;
  // The phased offer is a two-step price, not a flat discount, so it keeps its
  // own copy rather than the discounted/struck-through treatment.
  const isDiscounted = !isPhased && pricing.isDiscounted;

  return (
    <div className={styles.pricing_card}>
      <img
        src={orange_bg_check}
        alt="checked plan"
        className={styles.plan_checkmark}
      />
      <div className={styles.header}>
        <div className={styles.top_side_content}>
          <div className={styles.header_wrapper}>
            <div className={styles.header_logo_wrapper}>
              <img
                src={coachnow_logo}
                alt="CoachNow Logo"
                className={styles.site_logo}
              />
              <img
                src={academy_logo}
                alt="Academy"
                className={styles.pro_badge}
              />
            </div>
          </div>
          <p>{description}</p>
        </div>

        <div>
          <h3 className={styles.content_title}>
            {loading
              ? t("Applying your offer…")
              : !purchase
              ? trialHeadline
              : isDiscounted
              ? (
                <DiscountedPrice
                  discounted={discountedAmount}
                  original={base}
                  currency={currency}
                  interval={interval}
                />
              )
              : t("{{price}} per {{interval}}", {
                  price: formatPrice(
                    isPhased ? phasedFirstAmount : discountedAmount,
                    currency,
                  ),
                  interval,
                })}
          </h3>
          <p className={styles.content_description}>
            {loading ? null : purchase ? (
              // No trial, so no "Then …" lead-in: the phased offer spells out
              // its two steps, a flat price just names the billing period.
              isPhased ? (
                t("{{first}} per month for 12 months, then {{second}} per month", {
                  first: formatPrice(phasedFirstAmount, currency),
                  second: formatPrice(phasedSecondAmount, currency),
                })
              ) : (
                billing
              )
            ) : isPhased ? (
              t("trial_price_phased", {
                first: formatPrice(phasedFirstAmount, currency),
                second: formatPrice(phasedSecondAmount, currency),
              })
            ) : (
              <TrialPriceLine pricing={pricing} currency={currency} />
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PriceCard;
